import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  where,
  orderBy,
} from 'firebase/firestore';
import './MealPlanner.css';
import Drawer from './Drawer';
import PageHeader from './PageHeader';

function MealPlanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // États pour UnifiedSearchBar
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categories, setCategories] = useState([]);

  // États pour les recettes
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);

  // Ref pour éviter le double chargement au premier rendu (P1)
  const isFirstRender = useRef(true);

  // États pour le drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMealType, setSelectedMealType] = useState(null);

  // Recette en attente d'ajout (venant de Recipe.js via sessionStorage)
  const [pendingRecipe, setPendingRecipe] = useState(null);

  // Types de repas
  const mealTypes = [
    { id: 'breakfast', label: 'Petit-déjeuner' },
    { id: 'lunch', label: 'Déjeuner' },
    { id: 'dinner', label: 'Dîner' },
  ];

  // Couleurs par jour pour personnaliser l'affichage
  const dayColors = {
    0: '#5c6bc0', // Lundi (bleu)
    1: '#ff9800', // Mardi (orange)
    2: '#009688', // Mercredi (turquoise)
    3: '#8d6e63', // Jeudi (marron)
    4: '#7c4dff', // Vendredi (violet)
    5: '#2e7d32', // Samedi (vert)
    6: '#c2185b', // Dimanche (rose)
  };

  // Fonction utilitaire pour standardiser la conversion des timestamps
  const toJSDate = dateOrTimestamp => {
    if (dateOrTimestamp == null) return null;

    if (dateOrTimestamp instanceof Date) {
      const normalizedDate = new Date(dateOrTimestamp);
      normalizedDate.setMilliseconds(0);
      return normalizedDate;
    }

    if (
      dateOrTimestamp.toDate &&
      typeof dateOrTimestamp.toDate === 'function'
    ) {
      const date = dateOrTimestamp.toDate();
      date.setMilliseconds(0);
      return date;
    }

    if (typeof dateOrTimestamp === 'number') {
      const date = new Date(dateOrTimestamp);
      date.setMilliseconds(0);
      return date;
    }

    if (typeof dateOrTimestamp === 'string') {
      const date = new Date(dateOrTimestamp);
      if (!isNaN(date.getTime())) {
        date.setMilliseconds(0);
        return date;
      }
    }

    console.warn('Format de date invalide:', dateOrTimestamp);
    return null;
  };

  // Comparer deux dates (seulement année, mois, jour)
  const isSameDay = (date1, date2) => {
    date1 = toJSDate(date1);
    date2 = toJSDate(date2);

    if (!date1 || !date2) return false;

    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Obtenir le premier jour de la semaine (lundi)
  function getStartOfWeek(date) {
    const inputDate = toJSDate(date);

    if (!inputDate) {
      console.warn('Date invalide fournie à getStartOfWeek:', date);
      return new Date();
    }

    const result = new Date(inputDate);
    const day = result.getDay();
    const diff = result.getDate() - day + (day === 0 ? -6 : 1);

    result.setDate(diff);
    result.setHours(0, 0, 0, 0);

    return result;
  }

  // États pour la gestion des dates
  const [currentWeekStart, setCurrentWeekStart] = useState(
    getStartOfWeek(new Date())
  );
  const [weekDays, setWeekDays] = useState([]);

  // États pour les recettes et le planning
  const [mealPlan, setMealPlan] = useState({});

  // Charger les catégories de l'utilisateur
  const loadCategories = async () => {
    try {
      if (!auth.currentUser) return;

      const categoriesCollection = collection(
        db,
        'categoriesUtilisateur',
        auth.currentUser.uid,
        'categories'
      );

      const snapshot = await getDocs(categoriesCollection);

      const categoriesList = [];
      snapshot.forEach(doc => {
        categoriesList.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setCategories(categoriesList);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  // Charger toutes les recettes
  const loadRecipes = async () => {
    try {
      setLoadingRecipes(true);

      if (!auth.currentUser) return;

      const recipesCollection = collection(
        db,
        'recettesUtilisateur',
        auth.currentUser.uid,
        'recettes'
      );

      const recipesQuery = query(
        recipesCollection,
        orderBy('dateMiseAJour', 'desc')
      );

      const snapshot = await getDocs(recipesQuery);

      const recipesList = [];
      snapshot.forEach(doc => {
        recipesList.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setRecipes(recipesList);
      setFilteredRecipes(recipesList);
      setLoadingRecipes(false);
    } catch (error) {
      console.error('Erreur lors du chargement des recettes:', error);
      setLoadingRecipes(false);
    }
  };

  // Obtenir la couleur principale d'une recette
  const getRecipeMainColor = recipe => {
    if (!recipe || !recipe.categories || recipe.categories.length === 0) {
      return '#ff7043';
    }

    const primaryCategoryId = recipe.categories[0];

    // Si c'est une string (recette par défaut), retourner la couleur par défaut
    if (typeof primaryCategoryId === 'string') {
      return '#ff7043';
    }

    // Sinon, chercher la catégorie utilisateur
    const category = categories.find(cat => cat.id === primaryCategoryId);

    return category ? category.couleur : '#ff7043';
  };

  // Mise à jour des jours de la semaine
  const loadWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    setWeekDays(days);
  };

  // Formater une date en clé pour le planning (YYYY-MM-DD)
  const formatDateKey = date => {
    date = toJSDate(date);

    if (!date) {
      console.warn('Date invalide fournie à formatDateKey:', date);
      return 'date-invalide';
    }

    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  // Récupérer le planning de la semaine de la base de données
  const loadMealPlan = async () => {
    try {
      setLoading(true);
      setError('');

      if (!auth.currentUser) {
        setLoading(false);
        return;
      }

      // Dates de début et fin de la semaine
      const weekStart = new Date(currentWeekStart);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      // Créer une structure vide pour le planning de la semaine
      const emptyWeekPlan = {};
      for (let i = 0; i < 7; i++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + i);
        const dateKey = formatDateKey(day);

        emptyWeekPlan[dateKey] = {};
        mealTypes.forEach(type => {
          emptyWeekPlan[dateKey][type.id] = null;
        });
      }

      // Récupérer tous les plans pour la semaine
      const planningCollection = collection(
        db,
        'planningRepas',
        auth.currentUser.uid,
        'plans'
      );
      const q = query(
        planningCollection,
        where('date', '>=', weekStart),
        where('date', '<=', weekEnd)
      );

      const snapshot = await getDocs(q);

      // Si aucun plan n'est trouvé, initialiser un planning vide
      if (snapshot.empty) {
        setMealPlan(emptyWeekPlan);
        setLoading(false);
        return;
      }

      // Créer une copie du planning vide pour y ajouter les données
      const weekPlan = JSON.parse(JSON.stringify(emptyWeekPlan));

      // Collecter tous les IDs de recettes pour les charger en une seule fois
      const recipeIds = new Set();
      const planData = {};

      snapshot.forEach(doc => {
        const plan = doc.data();
        const planDate = toJSDate(plan.date);
        if (!planDate) return;

        const dateKey = formatDateKey(planDate);

        // Enregistrer les données du plan
        if (!planData[dateKey]) {
          planData[dateKey] = {
            id: doc.id,
            repas: {},
          };
        }

        // Traiter les repas
        if (plan.repas) {
          Object.entries(plan.repas).forEach(([mealType, meal]) => {
            if (meal && meal.idRecette) {
              recipeIds.add(meal.idRecette);

              planData[dateKey].repas[mealType] = {
                idRecette: meal.idRecette,
                nombrePersonnes: meal.nombrePersonnes || 1,
              };
            }
          });
        }
      });

      // Charger toutes les recettes en une seule fois
      const recipesData = {};
      if (recipeIds.size > 0) {
        const recipePromises = Array.from(recipeIds).map(async recipeId => {
          try {
            const recipeRef = doc(
              db,
              'recettesUtilisateur',
              auth.currentUser.uid,
              'recettes',
              recipeId
            );
            const recipeSnap = await getDoc(recipeRef);

            if (recipeSnap.exists()) {
              recipesData[recipeId] = {
                id: recipeId,
                ...recipeSnap.data(),
              };
              return true;
            } else {
              console.warn(`⚠️ Recette non trouvée: ${recipeId}`);
              return false;
            }
          } catch (err) {
            console.error(
              `❌ Erreur lors du chargement de la recette ${recipeId}:`,
              err
            );
            return false;
          }
        });

        await Promise.all(recipePromises);
      }

      // Construire le planning final avec les recettes
      Object.entries(planData).forEach(([dateKey, plan]) => {
        if (!weekPlan[dateKey]) return;

        Object.entries(plan.repas).forEach(([mealType, meal]) => {
          if (weekPlan[dateKey][mealType] === undefined) return;

          const recipeId = meal.idRecette;
          const recipe = recipesData[recipeId];

          if (recipe) {
            weekPlan[dateKey][mealType] = {
              id: plan.id,
              idRecette: recipeId,
              nombrePersonnes: meal.nombrePersonnes,
              recipe: recipe,
            };
          } else {
            console.warn(
              `⚠️ Recette ${recipeId} non trouvée dans les données chargées`
            );
            weekPlan[dateKey][mealType] = {
              id: plan.id,
              idRecette: recipeId,
              nombrePersonnes: meal.nombrePersonnes,
              recipe: null,
            };
          }
        });
      });

      setMealPlan(weekPlan);
      setLoading(false);
    } catch (err) {
      console.error('❌ Erreur lors du chargement du planning:', err);
      setError('Impossible de charger le planning. Veuillez réessayer.');
      setLoading(false);
    }
  };

  // Ajouter une recette au planning
  const addToPlan = async (recipeId, date, mealType, servings) => {
    try {
      const firestoreDate = new Date(date);
      firestoreDate.setHours(0, 0, 0, 0);

      const planningCollection = collection(
        db,
        'planningRepas',
        auth.currentUser.uid,
        'plans'
      );

      const startOfDay = new Date(firestoreDate);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(firestoreDate);
      endOfDay.setHours(23, 59, 59, 999);

      const q = query(
        planningCollection,
        where('date', '>=', startOfDay),
        where('date', '<', endOfDay)
      );

      const planSnapshot = await getDocs(q);

      let planId;

      if (!planSnapshot.empty) {
        planId = planSnapshot.docs[0].id;
        const planRef = doc(planningCollection, planId);
        const existingPlan = planSnapshot.docs[0].data();

        const updatedMeals = {
          ...(existingPlan.repas || {}),
          [mealType]: {
            idRecette: recipeId,
            nombrePersonnes: servings,
          },
        };

        await updateDoc(planRef, {
          repas: updatedMeals,
          dateMiseAJour: serverTimestamp(),
        });
      } else {
        const newPlan = {
          date: firestoreDate,
          repas: {
            [mealType]: {
              idRecette: recipeId,
              nombrePersonnes: servings,
            },
          },
          notes: '',
          dateCreation: serverTimestamp(),
          dateMiseAJour: serverTimestamp(),
        };

        const docRef = await addDoc(planningCollection, newPlan);
        planId = docRef.id;
      }

      return planId;
    } catch (err) {
      console.error("Erreur lors de l'ajout au planning:", err);
      setError("Erreur lors de l'ajout au planning. Veuillez réessayer.");
      return false;
    }
  };

  // Supprimer une recette du planning
  const removeFromPlan = async (dateKey, mealType) => {
    try {
      const dayPlan = mealPlan[dateKey];
      if (!dayPlan || !dayPlan[mealType]) return;

      const planId = dayPlan[mealType].id;
      if (!planId || planId.startsWith('temp-')) return;

      const planningCollection = collection(db, 'planningRepas', auth.currentUser.uid, 'plans');
      const planRef = doc(planningCollection, planId);

      // Construire les repas restants depuis le state — pas besoin de getDoc
      const remainingRepas = {};
      mealTypes.forEach(({ id }) => {
        if (id !== mealType && dayPlan[id] && dayPlan[id].idRecette) {
          remainingRepas[id] = {
            idRecette: dayPlan[id].idRecette,
            nombrePersonnes: dayPlan[id].nombrePersonnes,
          };
        }
      });

      if (Object.keys(remainingRepas).length === 0) {
        await deleteDoc(planRef);
      } else {
        await updateDoc(planRef, {
          repas: remainingRepas,
          dateMiseAJour: serverTimestamp(),
        });
      }

      return true;
    } catch (err) {
      console.error('Erreur lors de la suppression du planning:', err);
      setError('Erreur lors de la suppression du planning. Veuillez réessayer.');
      return false;
    }
  };

  // Fonction pour gérer la sélection d'une recette dans le drawer
  const handleRecipeSelection = recipe => {
    if (selectedDay && selectedMealType) {
      const recipeWithDetails = {
        ...recipe,
        nombrePersonnes: recipe.nombrePersonnes || 1,
      };

      const dateKey = formatDateKey(selectedDay);

      // Mise à jour optimiste : on met à jour l'UI immédiatement
      setMealPlan(prevMealPlan => {
        const updatedMealPlan = { ...prevMealPlan };

        if (!updatedMealPlan[dateKey]) {
          updatedMealPlan[dateKey] = {};
          mealTypes.forEach(type => {
            updatedMealPlan[dateKey][type.id] = null;
          });
        }

        // Mise à jour temporaire avec un ID temporaire
        updatedMealPlan[dateKey][selectedMealType] = {
          id: 'temp-' + Date.now(), // ID temporaire
          idRecette: recipe.id,
          nombrePersonnes: recipeWithDetails.nombrePersonnes,
          recipe: recipeWithDetails,
        };

        return updatedMealPlan;
      });

      // Fermer le drawer et afficher le message immédiatement
      setDrawerOpen(false);

      setMessage({
        type: 'success',
        text: `"${
          recipe.nom
        }" ajoutée au planning pour le ${formatDayName(selectedDay)}`,
      });

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);

      // Synchroniser avec Firebase en arrière-plan puis remplacer le temp-id
      const capturedDateKey = dateKey;
      const capturedMealType = selectedMealType;
      addToPlan(recipe.id, selectedDay, selectedMealType, recipeWithDetails.nombrePersonnes)
        .then(planId => {
          setMealPlan(prev => {
            const next = JSON.parse(JSON.stringify(prev));
            if (next[capturedDateKey]?.[capturedMealType]) {
              next[capturedDateKey][capturedMealType].id = planId;
            }
            return next;
          });
        })
        .catch(err => {
          console.error('Erreur lors de la sélection de la recette:', err);
          loadMealPlan();
          setError("Erreur lors de l'ajout de la recette au planning. Veuillez réessayer.");
        });
    } else {
      setError(
        "Veuillez sélectionner un jour et un type de repas avant d'ajouter une recette."
      );
    }
  };

  // Charger la recette en attente depuis sessionStorage (venant de Recipe.js)
  const loadPendingRecipe = async (recipeId, servings) => {
    try {
      const userId = auth.currentUser.uid;
      const recipeSnap = await getDoc(doc(db, 'recettesUtilisateur', userId, 'recettes', recipeId));
      if (recipeSnap.exists()) {
        setPendingRecipe({ id: recipeId, ...recipeSnap.data(), nombrePersonnes: servings });
      }
    } catch (error) {
      console.error('Erreur chargement recette en attente:', error);
    }
  };

  // Fonction pour ouvrir le drawer avec le contexte du repas sélectionné
  // Si une recette est en attente (depuis Recipe.js), l'ajouter directement au créneau cliqué
  const handleAddRecipe = (date, mealType) => {
    if (pendingRecipe) {
      const recipeWithDetails = { ...pendingRecipe, nombrePersonnes: pendingRecipe.nombrePersonnes || 1 };
      const dateKey = formatDateKey(date);

      setMealPlan(prevMealPlan => {
        const updatedMealPlan = { ...prevMealPlan };
        if (!updatedMealPlan[dateKey]) {
          updatedMealPlan[dateKey] = {};
          mealTypes.forEach(type => { updatedMealPlan[dateKey][type.id] = null; });
        }
        updatedMealPlan[dateKey][mealType] = {
          id: 'temp-' + Date.now(),
          idRecette: pendingRecipe.id,
          nombrePersonnes: recipeWithDetails.nombrePersonnes,
          recipe: recipeWithDetails,
        };
        return updatedMealPlan;
      });

      setPendingRecipe(null);
      setMessage({ type: 'success', text: `"${pendingRecipe.nom}" ajoutée au planning pour le ${formatDayName(date)}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);

      const capturedDateKey = formatDateKey(date);
      const capturedMealType = mealType;
      addToPlan(pendingRecipe.id, date, mealType, recipeWithDetails.nombrePersonnes)
        .then(planId => {
          setMealPlan(prev => {
            const next = JSON.parse(JSON.stringify(prev));
            if (next[capturedDateKey]?.[capturedMealType]) {
              next[capturedDateKey][capturedMealType].id = planId;
            }
            return next;
          });
        })
        .catch(err => {
          console.error('Erreur lors de l\'ajout de la recette au planning:', err);
          loadMealPlan();
          setError("Erreur lors de l'ajout de la recette au planning. Veuillez réessayer.");
        });
    } else {
      setSelectedDay(date);
      setSelectedMealType(mealType);
      setDrawerOpen(true);
    }
  };

  // Fonction pour générer le titre du drawer
  const getDrawerTitle = () => {
    if (!selectedDay || !selectedMealType) return 'Sélectionner une recette';

    const mealTypeLabel =
      mealTypes.find(type => type.id === selectedMealType)?.label || 'repas';

    const dayName = selectedDay.toLocaleDateString('fr-FR', {
      weekday: 'long',
    });

    return `Vos envies pour le ${mealTypeLabel} du ${dayName}`;
  };

  // Supprimer une recette du planning
  const handleRemoveFromPlan = (dateKey, mealType) => {
    // Mise à jour optimiste : on supprime immédiatement de l'UI
    setMealPlan(prevMealPlan => {
      const newMealPlan = JSON.parse(JSON.stringify(prevMealPlan));

      if (newMealPlan[dateKey]) {
        newMealPlan[dateKey][mealType] = null;
      }

      return newMealPlan;
    });

    // Ensuite, synchroniser avec Firebase en arrière-plan
    removeFromPlan(dateKey, mealType).catch(err => {
      console.error('Erreur lors de la suppression:', err);
      // En cas d'erreur, on recharge le planning
      loadMealPlan();
    });
  };

  // Fonctions de navigation temporelle
  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    const newStartDate = getStartOfWeek(newDate);
    setCurrentWeekStart(newStartDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    const newStartDate = getStartOfWeek(newDate);
    setCurrentWeekStart(newStartDate);
  };

  const goToToday = () => {
    const today = new Date();
    const newStartDate = getStartOfWeek(today);
    setCurrentWeekStart(newStartDate);
  };

  // Générer la liste de courses
  const generateShoppingList = () => {
    sessionStorage.setItem(
      'planningDates',
      JSON.stringify({
        startDate: currentWeekStart,
        endDate: new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000),
      })
    );

    navigate('/shopping-list');
  };

  // Utilitaires de formatage de date
  const formatDate = date => {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
    });
  };

  const formatDayName = date => {
    return date.toLocaleDateString('fr-FR', { weekday: 'long' });
  };

  // Formater la plage de dates pour l'affichage (desktop)
  const formatWeekRange = () => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(endDate.getDate() + 6);

    const startDay = currentWeekStart.getDate();
    const endDay = endDate.getDate();

    const startMonth = currentWeekStart.toLocaleDateString('fr-FR', { month: 'long' });
    const endMonth = endDate.toLocaleDateString('fr-FR', { month: 'long' });

    if (startMonth === endMonth) {
      return `${startDay} au ${endDay} ${startMonth} ${currentWeekStart.getFullYear()}`;
    } else {
      return `${startDay} ${startMonth} au ${endDay} ${endMonth} ${currentWeekStart.getFullYear()}`;
    }
  };

  // Version courte pour mobile : "17 – 23 avr."
  const formatWeekRangeShort = () => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(endDate.getDate() + 6);

    const startDay = currentWeekStart.getDate();
    const endDay = endDate.getDate();
    const month = endDate.toLocaleDateString('fr-FR', { month: 'short' });

    return `${startDay} – ${endDay} ${month}.`;
  };

  // Calculer le numéro de semaine ISO 8601
  const getWeekNumber = date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNo;
  };

  // Vérifier si une date est aujourd'hui
  const isToday = date => {
    const today = new Date();
    return isSameDay(date, today);
  };

  // Vérifier si une date est un weekend
  const isWeekend = date => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // Obtenir la couleur pour un jour spécifique
  const getDayColor = dayIndex => {
    return dayColors[dayIndex] || '#ff7043';
  };

  // Charger les données au démarrage
  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/');
      return;
    }

    loadWeekDays();
    loadMealPlan();
    loadCategories();
  }, [currentWeekStart, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recharger les données quand l'utilisateur revient sur la page (pas au premier rendu)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (auth.currentUser) {
      loadWeekDays();
      loadMealPlan();
      loadCategories();
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // Détecter une recette en attente transmise depuis Recipe.js
  useEffect(() => {
    const stored = sessionStorage.getItem('selectedRecipe');
    if (stored && auth.currentUser) {
      sessionStorage.removeItem('selectedRecipe');
      const { recipeId, servings } = JSON.parse(stored);
      loadPendingRecipe(recipeId, servings);
    }
  }, [location]);

  // Nettoyer les erreurs lors des changements de semaine
  useEffect(() => {
    setError('');
    setMessage({ type: '', text: '' });
  }, [currentWeekStart, location]);

  // Filtrer les recettes quand la recherche change
  useEffect(() => {
    if (recipes.length === 0) return;

    const filterRecipes = () => {
      return recipes.filter(recipe => {
        const matchesSearchTerm =
          searchTerm.trim() === '' ||
          recipe.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (recipe.description &&
            recipe.description
              .toLowerCase()
              .includes(searchTerm.toLowerCase()));

        const matchesCategories =
          selectedCategories.length === 0 ||
          selectedCategories.every(
            catId => recipe.categories && recipe.categories.includes(catId)
          );

        return matchesSearchTerm && matchesCategories;
      });
    };

    setFilteredRecipes(filterRecipes());
  }, [searchTerm, selectedCategories, recipes]);

  // Charger les recettes/catégories à la première ouverture du drawer uniquement
  useEffect(() => {
    if (drawerOpen) {
      if (recipes.length === 0) loadRecipes();
      if (categories.length === 0) loadCategories();
    }
  }, [drawerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="loading">Chargement du planning...</div>;
  }

  return (
    <div className="meal-planner-container">
      {/* Entête fixe - Nouvelle version épurée */}
      <PageHeader
        title="Mon Planning"
        backTo="/"
        backAriaLabel="Retour à l'accueil"
        sticky={true}
        actions={
          !weekDays.some(day => isToday(day)) && (
            <button className="header-today-button" onClick={goToToday}>
              Cette semaine
            </button>
          )
        }
      >
        {/* Barre de navigation */}
        <div className="week-navigation">
          <button
            className="nav-arrow-button"
            onClick={goToPreviousWeek}
            title="Semaine précédente"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
          </button>

          <div className="week-info-center">
            <div className="week-range-display">
              <span className="week-range-full">{formatWeekRange()}</span>
              <span className="week-range-short">{formatWeekRangeShort()}</span>
            </div>
            <div className="week-meta-info">
              <span className="week-number">
                📅 Semaine {getWeekNumber(currentWeekStart)}
                {weekDays.some(day => isToday(day)) && (
                  <span className="week-current-indicator"> ●</span>
                )}
              </span>
            </div>
          </div>

          <button
            className="nav-arrow-button"
            onClick={goToNextWeek}
            title="Semaine suivante"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      </PageHeader>

      {/* Bannière recette en attente (depuis Recipe.js) */}
      {pendingRecipe && (
        <div className="pending-recipe-banner">
          <span>📌 Ajouter <strong>{pendingRecipe.nom}</strong> — cliquez sur un créneau</span>
          <button className="pending-recipe-cancel" onClick={() => setPendingRecipe(null)}>✕ Annuler</button>
        </div>
      )}

      {/* Zone scrollable principale */}
      <div className="meal-planner-scrollable">
        {/* Message d'erreur/succès */}
        {message.text && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}
        {error && <div className="error-message">{error}</div>}

        {/* Planning par jour */}
        <div className="day-based-planner">
          {weekDays.map((day, index) => {
            const dateKey = formatDateKey(day);
            const dayPlan = mealPlan[dateKey] || {};
            const dayOfWeek = day.getDay() || 7;
            const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            const dayColor = getDayColor(dayIndex);

            return (
              <div
                key={dateKey}
                className={`day-plan ${isToday(day) ? 'today' : ''} ${
                  isWeekend(day) ? 'weekend' : ''
                }`}
              >
                <div
                  className="day-color-bar"
                  style={{ backgroundColor: dayColor }}
                ></div>
                <div className="day-header">
                  <h3>{formatDayName(day)}{isToday(day) && <span className="today-badge">Aujourd'hui</span>}</h3>
                  <span className="day-date">{formatDate(day)}</span>
                </div>
                <div className="day-meals">
                  {mealTypes.map(mealType => {
                    const meal = dayPlan[mealType.id];
                    const isSelected =
                      selectedDay &&
                      formatDateKey(day) === formatDateKey(selectedDay) &&
                      mealType.id === selectedMealType;
                    return (
                      <div
                        key={`${dateKey}-${mealType.id}`}
                        className={`meal-slot ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="meal-type">{mealType.label}</div>
                        {meal && meal.recipe ? (
                          <div className="planned-meal">
                            <div
                              className="planned-recipe-name"
                              onClick={() => navigate(`/recipe/${meal.idRecette}`)}
                              title="Voir la recette"
                            >
                              {meal.recipe.nom}
                            </div>
                            <div className="meal-actions">
                              <button
                                className="meal-action-button meal-action-change"
                                onClick={() => handleAddRecipe(day, mealType.id)}
                                title="Changer la recette"
                              >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                </svg>
                              </button>
                              <button
                                className="meal-action-button meal-action-remove"
                                onClick={() => handleRemoveFromPlan(dateKey, mealType.id)}
                                title="Supprimer"
                              >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="empty-meal"
                            onClick={() => handleAddRecipe(day, mealType.id)}
                          >
                            + Ajouter une recette
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer fixe - Bouton génération liste de courses */}
      <div className="meal-planner-fixed-footer">
        <div className="generate-shopping-list">
          <button
            className="shopping-list-button"
            onClick={generateShoppingList}
          >
            🛒 Générer ma liste de courses
          </button>
        </div>
      </div>

      {/* Drawer pour l'ajout de recettes */}
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={getDrawerTitle()}
      >
        <div className="drawer-subtitle">
          <h3>Votre liste de recettes</h3>
        </div>

        <div className="drawer-search-section">
          <div className="search-input-wrapper">
            <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              className="recipe-search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Rechercher une recette..."
            />
            {searchTerm && (
              <button className="search-clear-btn" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>

          {categories.length > 0 && (
            <div className="category-chips-row">
              <button
                className={`category-chip ${selectedCategories.length === 0 ? 'chip-all-active' : 'chip-all'}`}
                onClick={() => setSelectedCategories([])}
              >
                Tout
              </button>
              {categories.map(cat => {
                const isActive = selectedCategories.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    className={`category-chip ${isActive ? 'chip-active' : ''}`}
                    style={isActive
                      ? { backgroundColor: cat.couleur, borderColor: cat.couleur, color: '#fff' }
                      : { borderColor: cat.couleur + '99', color: cat.couleur }
                    }
                    onClick={() => {
                      if (isActive) {
                        setSelectedCategories(selectedCategories.filter(id => id !== cat.id));
                      } else {
                        setSelectedCategories([...selectedCategories, cat.id]);
                      }
                    }}
                  >
                    {cat.nom}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Affichage des recettes filtrées */}
        {loadingRecipes ? (
          <div className="loading-recipes">Chargement des recettes...</div>
        ) : filteredRecipes.length > 0 ? (
          <div className="drawer-recipes-grid">
            {filteredRecipes.map(recipe => {
              const recipeColor = getRecipeMainColor(recipe);
              const totalTime =
                (recipe.tempsPreparation || 0) + (recipe.tempsCuisson || 0);

              return (
                <div
                  key={recipe.id}
                  className="drawer-recipe-card"
                  onClick={() => handleRecipeSelection(recipe)}
                >
                  <div
                    className="recipe-color-bar"
                    style={{ backgroundColor: recipeColor }}
                  ></div>
                  <div className="drawer-recipe-content">
                    <h4>{recipe.nom}</h4>
                    <div className="drawer-recipe-stats">
                      <div className="recipe-stat">
                        <span className="recipe-stat-icon">⏱️</span>
                        <span>{totalTime} min</span>
                      </div>
                      {recipe.difficulte && (
                        <div className="recipe-stat">
                          <span className="recipe-stat-icon">
                            {recipe.difficulte === 'Facile'
                              ? '⚡'
                              : recipe.difficulte === 'Moyen'
                              ? '🔄'
                              : '🔥'}
                          </span>
                          <span>{recipe.difficulte}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-results">
            {searchTerm || selectedCategories.length > 0 ? (
              <>
                <p>Aucune recette ne correspond à votre recherche.</p>
                <p>Essayez de modifier vos critères de recherche.</p>
                <button
                  className="action-button"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategories([]);
                  }}
                >
                  Réinitialiser les filtres
                </button>
              </>
            ) : (
              <>
                <p>Vous n'avez pas encore de recettes.</p>
                <p style={{ fontSize: '13px', marginTop: '4px' }}>Créez-en une pour commencer à planifier vos repas.</p>
                <button
                  className="action-button"
                  onClick={() => navigate('/add-recipe')}
                >
                  Créer ma première recette
                </button>
              </>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default MealPlanner;
